import { useMutation } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { updateDateRangeEmissions, updateDateRangeConsumption, updateDateRangeFinancials } from "api";
import { generateXAxisCategories, generateXAxisData, getEmissionType, sortedDates } from "utils";

interface UseTotalDataParams {
    searchFilters: any;
    dateRangeStartDate: any;
    dateRangeEndDate: any;
    dateRangeType: string;
    currentMainTab: string;
    subTypeChart: string;
    enabled?: boolean;
}

interface UseTotalDataReturn {
    totalData: any[];
    isLoadingTotal: boolean;
    error: any;
}

export const useTotalData = ({
    searchFilters,
    dateRangeStartDate,
    dateRangeEndDate,
    dateRangeType,
    currentMainTab,
    subTypeChart,
    enabled = true,
}: UseTotalDataParams): UseTotalDataReturn => {
    const [totalData, setTotalData] = useState<any[]>([]);
    const [isLoadingTotal, setIsLoadingTotal] = useState(false);
    const [error, setError] = useState<any>(null);

    const apiFn = currentMainTab === "GHG Emissions"
        ? updateDateRangeEmissions
        : currentMainTab === "Consumption"
            ? updateDateRangeConsumption
            : updateDateRangeFinancials;

    const { mutateAsync: updateTotalDateRange } = useMutation(apiFn);

    // useRef keeps a stable reference so the effect doesn't re-fire on every render
    // (useMutation recreates mutateAsync on every render)
    const updateTotalDateRangeRef = useRef(updateTotalDateRange);
    updateTotalDateRangeRef.current = updateTotalDateRange;

    const isEmissionsChart = subTypeChart === "location" || subTypeChart === "market";

    useEffect(() => {
        if (!enabled) return;

        const fetchTotalData = async () => {
            setIsLoadingTotal(true);
            setError(null);

            try {
                await updateTotalDateRangeRef.current({
                    dateRangeRequest: {
                        ...searchFilters,
                        compareFromDate: dateRangeStartDate.toISOString().substring(0, 10),
                        compareToDate: dateRangeEndDate.toISOString().substring(0, 10),
                        groupBy: dateRangeType
                    },
                    chartType: 'total'
                }, {
                    onSuccess: (response: any) => {
                        const { total } = response.data;
                        const totalDataArray = total ? Object.values(total).flat() : [];
                        // generateXAxisCategories derives de-duplicated x-axis labels from totalDataArray
                        // (e.g. "Jan '24" for MONTHLY, "Q1 '24 FY" for QUARTERLY, "2024 FY" for YEARLY),
                        // but it uses a Set internally so the resulting array is NOT ordered.
                        // sortedDates re-sorts those labels chronologically; the ordered result is then
                        // passed to generateXAxisData as the x-axis keys used to aggregate totals per label.
                        const emissionType = getEmissionType(isEmissionsChart, currentMainTab === "Consumption", subTypeChart);
                        const categories = sortedDates(generateXAxisCategories(totalDataArray, dateRangeType));
                        const aggregated = generateXAxisData({ total: totalDataArray }, categories, dateRangeType, emissionType);
                        setTotalData(aggregated.total);
                    },
                    onError: (err: any) => {
                        setError(err);
                    }
                });
            } catch (err) {
                setError(err);
            } finally {
                setIsLoadingTotal(false);
            }
        };

        fetchTotalData();
    }, [searchFilters, dateRangeStartDate, dateRangeEndDate, dateRangeType, currentMainTab, subTypeChart, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        totalData,
        isLoadingTotal,
        error
    };
}; 